import { useEffect, useState } from "react";
import { useGameStats } from "@/hooks/useGameStats";

export default function ScoreSection() {
  const { score, attempts, streak } = useGameStats();

  const [animatedScore, setAnimatedScore] = useState(score);
  const [animatedAttempts, setAnimatedAttempts] = useState(attempts);
  const [animatedStreak, setAnimatedStreak] = useState(streak);

  // Animate numbers when they change
  useEffect(() => {
    const duration = 600;
    const startTime = Date.now();
    const startScore = animatedScore;
    const startAttempts = animatedAttempts;
    const startStreak = animatedStreak;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic

      setAnimatedScore(Math.round(startScore + (score - startScore) * eased));
      setAnimatedAttempts(Math.round(startAttempts + (attempts - startAttempts) * eased));
      setAnimatedStreak(Math.round(startStreak + (streak - startStreak) * eased));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [score, attempts, streak]);

  const stats = [
    {
      label: "Parties Jouées",
      value: animatedAttempts,
      icon: "ri-gamepad-line",
      color: "bg-orange-100",
      iconColor: "text-orange-500",
    },
    {
      label: "Victoires",
      value: animatedScore,
      icon: "ri-trophy-line",
      color: "bg-green-100",
      iconColor: "text-green-500",
    },
    {
      label: "Précision",
      value:
        animatedAttempts > 0
          ? Math.round((animatedScore / animatedAttempts) * 100)
          : 0,
      icon: "ri-medal-line",
      color: "bg-blue-100",
      iconColor: "text-blue-500",
      suffix: "%",
    },
  ];

  return (
    <section
      id="score"
      className="relative min-h-[80vh] bg-white flex items-center justify-center py-20"
    >
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-4xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-brand-text mb-4">
              Tableau de Bord
            </h2>
            <p className="text-brand-muted text-lg max-w-lg mx-auto">
              Suivez tes progrès dans l'apprentissage de l'heure
            </p>
          </div>

          {/* Stats cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="bg-brand-light rounded-2xl p-6 text-center transition-transform hover:-translate-y-1"
              >
                <div
                  className={`w-14 h-14 ${stat.color} rounded-xl flex items-center justify-center mx-auto mb-4`}
                >
                  <i className={`${stat.icon} ${stat.iconColor} text-2xl`} />
                </div>
                <p className="font-display text-4xl font-bold text-brand-text mb-1">
                  {stat.value}
                  {stat.suffix || ""}
                </p>
                <p className="text-brand-muted text-sm">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Encouragement card */}
          <div className="bg-gradient-to-r from-brand-primary to-brand-primary/80 rounded-2xl p-8 md:p-12 text-center text-white">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-star-line text-3xl" />
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-bold mb-4">
              Continue comme ça !
            </h3>
            <p className="text-white/80 text-lg max-w-lg mx-auto mb-6">
              Chaque essai te rapproche un peu plus de maîtriser l'heure. Ne
              lâche rien !
            </p>
            <button
              onClick={() =>
                document
                  .getElementById("jeu")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="px-8 py-3 bg-brand-accent text-white font-semibold rounded-xl hover:bg-brand-accent/90 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-line mr-2" />
              Nouvelle Partie
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}